/**
 * CoachChat — shared chat surface for The Coach (used by the editor panel and
 * the full Coach page). Loads a session's messages, sends new ones, renders
 * replies with MarkdownLite. Sessions are created lazily via ensureSession.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MarkdownLite } from "@/components/MarkdownLite";
import { Send, Loader2, Compass } from "lucide-react";

const EXAMPLES = [
  "I'm stuck on my ending.",
  "My main character feels flat and I don't know why.",
  "My second act is sagging.",
  "I don't know what my story is really about.",
];

export function CoachChat({ sessionId, ensureSession, onTitle, onBeforeSend, prefill }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const sidRef = useRef(sessionId);
  const prefillNonceRef = useRef(null);

  // A "Work on this with the Coach" handoff pre-fills the input; the writer
  // stays in control — they can edit before sending.
  useEffect(() => {
    if (!prefill?.text || prefill.nonce === prefillNonceRef.current) return;
    prefillNonceRef.current = prefill.nonce;
    setInput(prefill.text);
    setTimeout(() => inputRef.current?.focus(), 60);
  }, [prefill]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  }, [input]);

  useEffect(() => {
    sidRef.current = sessionId;
    setMessages([]);
    if (!sessionId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/coach/sessions/${sessionId}`)
      .then(({ data }) => { if (!cancelled) setMessages(data.session.messages || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking]);

  const send = useCallback(async (q) => {
    const text = (q ?? input).trim();
    if (!text || thinking) return;
    setThinking(true);
    setInput("");
    setMessages((ms) => [...ms, { role: "user", text }]);
    try {
      try { await onBeforeSend?.(); } catch { /* draft flush is best-effort */ }
      let sid = sidRef.current;
      if (!sid) {
        sid = await ensureSession();
        sidRef.current = sid;
      }
      const { data } = await api.post(`/coach/sessions/${sid}/messages`, { text });
      setMessages((ms) => [...ms, data.reply]);
      onTitle?.(data.title, sid);
    } catch (e) {
      if (e.response?.status === 402) toast.error("Your subscription is inactive.");
      else toast.error(formatApiError(e.response?.data?.detail) || "The Coach couldn't respond just now.");
      setMessages((ms) => ms.slice(0, -1));
      setInput(text);
    } finally { setThinking(false); }
  }, [input, thinking, ensureSession, onTitle, onBeforeSend]);

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="coach-chat">
      <div ref={scrollRef} className="flex-1 overflow-auto px-4 py-3 space-y-3" data-testid="coach-messages">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)] py-6 justify-center" data-testid="coach-loading">
            <Loader2 size={15} className="animate-spin" /> Loading…
          </div>
        )}
        {!loading && messages.length === 0 && !thinking && (
          <div className="text-sm text-[var(--ink-secondary)]" data-testid="coach-empty">
            <div className="flex items-center gap-2 mb-2 text-[var(--ink-primary)]">
              <Compass size={16} className="text-[var(--brand)]" />
              <span className="font-display text-base">What are you wrestling with?</span>
            </div>
            <p className="mb-3 leading-relaxed">I won't write your story or hand you answers — the breakthrough has to be yours. I'll ask the questions that get you there, and teach craft along the way.</p>
            <p className="text-[11px] uppercase tracking-widest text-[var(--ink-muted)] mb-1.5">Start with</p>
            <div className="space-y-1.5">
              {EXAMPLES.map((ex, i) => (
                <button key={i} onClick={() => send(ex)} className="block w-full text-left px-3 py-2 rounded-lg border border-[var(--line)] hover:border-[var(--brand)]/50 hover:bg-[var(--warm-accent)] text-[var(--ink-primary)]" data-testid={`coach-example-${i}`}>“{ex}”</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"} data-testid={`coach-msg-${m.role}-${i}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-[var(--brand)] text-white rounded-br-sm" : "bg-[var(--warm-accent)] text-[var(--ink-primary)] rounded-bl-sm"}`}>
              {m.role === "user" ? m.text : <MarkdownLite text={m.text} />}
            </div>
          </div>
        ))}
        {thinking && (
          <div className="flex justify-start" data-testid="coach-thinking">
            <div className="bg-[var(--warm-accent)] rounded-2xl rounded-bl-sm px-3.5 py-2.5"><Loader2 size={15} className="animate-spin text-[var(--ink-secondary)]" /></div>
          </div>
        )}
      </div>
      <div className="border-t border-[var(--line)] p-3 shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1} placeholder="Talk it through…"
            className="flex-1 resize-none overflow-y-auto bg-[var(--canvas)] border border-[var(--line)] rounded-xl px-3 py-2 text-sm outline-none focus:border-[var(--brand)]"
            data-testid="coach-input"
          />
          <button onClick={() => send()} disabled={thinking || !input.trim()} className="btn-brand p-2.5 rounded-xl disabled:opacity-40" data-testid="coach-send"><Send size={16} /></button>
        </div>
      </div>
    </div>
  );
}
